import { Title } from 'react-head'
import styled from 'styled-components'
import { Button } from '@bm-js/atlascloud-ui'
import { FormEventHandler, useMemo, useState } from 'react'
import { breakPointsCss, colors, measurments } from '../utils/config'
import LinkButton from '../components/LinkButton'
import Input from '../components/Input'
import Loader from '../components/Loader'
import { fetcher } from '../utils/fetcher'
import { useActions, useToast, useToken, useUser } from '../utils/hooks'
import { ChangeEvent, LucideIconName, UserBM } from '@bm-js/atlascloud-shared'
import { _pick } from '@naturalcycles/js-lib/object'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { useTr } from '../utils/useTr'
import LanguageSelector from '../components/LanguageSelector'

const StyledAccount = styled.div`
  .card-container {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    .card {
      background: ${colors.white};
      padding: 1rem;
      min-width: 400px;
      border-radius: ${measurments.borderRadius}px;
      box-shadow: 0px 32px 64px -12px rgba(16, 24, 40, 0.2);
      .item {
        margin-bottom: 1rem;
        p {
          margin: 0;
          color: ${colors.gray700};
        }
      }
      .actions {
        gap: 0.5rem;
      }
    }
    ul {
      padding-left: 1rem;
    }
  }
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  ${breakPointsCss.tablet} {
    .headers {
      display: block;
    }
    .card-container {
      display: block;
      .card {
        min-width: revert;
        width: 100%;
        margin-bottom: 1rem;
      }
    }
  }
`

const fallbackUser: Partial<UserBM> = {
  fullName: '',
  email: '',
}

const Account = () => {
  const toast = useToast()
  const token = useToken()
  const user = useUser()
  const { signOut } = useActions()
  const { tr } = useTr()

  const initialPasswordData = {
    currentPassword: '',
    newPassword: '',
    newPasswordRepeat: '',
  }

  //States
  const [loading, setLoading] = useState(false)
  const [userFormData, setUserFormData] = useState<Partial<UserBM>>(
    _pick({ ...fallbackUser, ...user }, ['fullName', 'email'])
  )
  const [passwordFormData, setPasswordFormData] = useState(initialPasswordData)
  const [mode, setMode] = useState<'view' | 'account' | 'pw'>('view')

  const updateUserInit = () => {
    setMode('account')
  }

  const handleUserChange = (e: ChangeEvent) => {
    setUserFormData({
      ...userFormData,
      [e.target.name]: e.target.value,
    })
  }

  const handlePasswordDataChange = (e: ChangeEvent) => {
    setPasswordFormData({
      ...passwordFormData,
      [e.target.name]: e.target.value,
    })
  }

  const updateUser: FormEventHandler = async (e) => {
    e.preventDefault()
    setLoading(true)
    const { err } = await fetcher({
      path: 'customer/self/user-information',
      method: 'POST',
      body: userFormData,
      auth: token,
    })
    if (err) {
      setLoading(false)
      toast.error(tr(TrKey.accountFailedToUpdateInfo))
      return
    }
    toast.success(tr(TrKey.accountUpdatedInfo))
    signOut()
  }

  const changePassword: FormEventHandler = async (e) => {
    e.preventDefault()
    if (passwordFormData.newPassword !== passwordFormData.newPasswordRepeat) {
      toast.error(tr(TrKey.accountPasswordsDoNotMatch))
      return
    }
    setLoading(true)
    const { err } = await fetcher({
      path: 'customer/self/password',
      method: 'POST',
      body: passwordFormData,
      auth: token,
    })
    if (err) {
      toast.error(tr(TrKey.accountFailedToUpdatePassword))
      toast.error(tr(TrKey.accountCheckPasswords))
      setLoading(false)
      return
    }
    toast.success(tr(TrKey.accountSignInAgain))
    toast.success(tr(TrKey.accountPasswordUpdated))
    signOut()
  }

  const title = useMemo(() => {
    return tr(TrKey.accountSettings) + ' | Atlascloud'
  }, [tr])

  if (loading)
    return (
      <StyledAccount>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledAccount>
    )

  return (
    <StyledAccount className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb items-start mbottom1 flex'>
        <div>
          <h1 className='m0 primblue'>{tr(TrKey.accountSettings)}</h1>
          {mode !== 'view' && (
            <p className='red m0'>{tr(TrKey.accountUpdateDisclaimer)}</p>
          )}
        </div>
        <div className='flex gap-4'>
          <LinkButton icon={LucideIconName.Pen} onClick={() => setMode('pw')}>
            {tr(TrKey.accountChangePassword)}
          </LinkButton>
          <Button onClick={updateUserInit}>
            {tr(TrKey.accountUpdateInfo)}
          </Button>
        </div>
      </div>
      <br />

      <LanguageSelector />

      <div className='card-container'>
        {mode === 'view' && (
          <div className='card'>
            <div className='item'>
              <p className='b'>{tr(TrKey.name)}</p>
              <p>{user?.fullName}</p>
            </div>
            <div className='item'>
              <p className='b'>{tr(TrKey.emailAddress)}</p>
              <p>{user?.email}</p>
            </div>
          </div>
        )}
        {mode === 'account' && (
          <form className='card' onSubmit={updateUser}>
            <Input
              required
              type='text'
              label={tr(TrKey.name)}
              desc={tr(TrKey.accountNameDescription)}
              placeholder={tr(TrKey.accountNamePlaceholder)}
              value={userFormData.fullName}
              id='fullName'
              name='fullName'
              onChange={handleUserChange}
            />
            <Input
              required
              type='email'
              label={tr(TrKey.emailAddress)}
              desc={tr(TrKey.accountEmailDescription)}
              placeholder='example@mail.com'
              value={userFormData.email}
              id='email'
              name='email'
              onChange={handleUserChange}
            />

            <div className='ac jce actions flex'>
              <Button type='submit'>{tr(TrKey.save)}</Button>
              <Button danger onClick={() => setMode('view')}>
                {tr(TrKey.cancel)}
              </Button>
            </div>
          </form>
        )}
        {mode === 'pw' && (
          <>
            <form className='card' onSubmit={changePassword}>
              <Input
                required
                type='password'
                label={tr(TrKey.accountCurrentPassword)}
                desc={tr(TrKey.accountCurrentPasswordDescription)}
                value={passwordFormData.currentPassword}
                id='currentPassword'
                name='currentPassword'
                onChange={handlePasswordDataChange}
              />
              <Input
                required
                type='password'
                label={tr(TrKey.accountNewPassword)}
                desc={tr(TrKey.accountNewPasswordDescription)}
                value={passwordFormData.newPassword}
                id='newPassword'
                name='newPassword'
                onChange={handlePasswordDataChange}
              />
              <Input
                required
                type='password'
                label={tr(TrKey.accountNewPasswordRepeat)}
                desc={tr(TrKey.accountNewPasswordRepeatDescription)}
                value={passwordFormData.newPasswordRepeat}
                id='newPasswordRepeat'
                name='newPasswordRepeat'
                onChange={handlePasswordDataChange}
              />
              <div className='ac jce actions flex'>
                <Button type='submit'>{tr(TrKey.save)}</Button>
                <Button danger onClick={() => setMode('view')}>
                  {tr(TrKey.cancel)}
                </Button>
              </div>
            </form>
            <div className='card'>
              <p className='b s m0'>{tr(TrKey.accountPasswordRequirements)}</p>
              <ul>
                <li>{tr(TrKey.accountPasswordRequirementsItem1)}</li>
                <li>{tr(TrKey.accountPasswordRequirementsItem2)}</li>
                <li>{tr(TrKey.accountPasswordRequirementsItem3)}</li>
                <li>{tr(TrKey.accountPasswordRequirementsItem4)}</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </StyledAccount>
  )
}

export default Account
